import { z } from "zod";

// Auth validations
export const loginSchema = z.object({
  // GitHub login doesn't require form fields, but we can add email/password later
});

export const registerSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters"),
  email: z
    .string()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters"),
  githubUsername: z
    .string()
    .min(1, "GitHub username is required")
    .max(255, "GitHub username must be less than 255 characters"),
});

// User profile validations
export const updateProfileSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters")
    .optional(),
  email: z
    .string()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .optional(),
});

const portSchema = z
  .preprocess(
    (v) => (typeof v === "number" && !Number.isNaN(v) ? v : undefined),
    z
      .number()
      .int()
      .min(1, "Number must be greater than 1")
      .max(65535, "Port must be ≤ 65535"),
  )
  .optional();

export const createDeploySchema = z
  .object({
    deployType: z.enum(["CDN_SITE", "SHARED_COMPUTE_INSTANCE"], {
      required_error: "Please select a deploy type",
    }),
    sourceType: z.enum(["github", "upload"], {
      required_error: "Please select a source type",
    }),
    repoName: z.string().min(1, "Invalid repository name"),
    repoUrl: z.string().url("Invalid repository URL").optional(),
    branch: z.string().min(1, "Please select a branch"),
    installationId: z.number().optional(),
    autoDeployEnabled: z.boolean(),

    port: portSchema,

    region: z.string().min(1, "Please select a region"),
    env: z.string().min(1, "Please select an environment"),
    rootDirectory: z.string().optional(),
    gitToken: z.any().optional(),
    files: z.any().optional(),
    buildCommand: z.string().optional(),
    outputDirectory: z.string().optional(),
    environmentVariables: z
      .array(
        z.object({
          key: z.string().min(1, "Key is required"),
          value: z.string(),
        }),
      )
      .optional(),
  })
  .refine(
    (data) => {
      if (data.sourceType === "github")
        return !!(data.repoUrl && data.repoName && data.installationId);
      if (data.sourceType === "upload") return !!data.files;
      return false;
    },
    {
      message: "Please select a repository or upload files",
      path: ["sourceType"],
    },
  )
  .superRefine((data, ctx) => {
    if (
      data.deployType === "SHARED_COMPUTE_INSTANCE" &&
      (data.port === undefined || data.port === null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["port"],
        message: "Port is required for server apps",
      });
    }
  });

// Deploy settings validations
export const updateDeploySettingsSchema = z.object({
  id: z.string(),
  buildCommand: z.string().optional(),
  gitToken: z.any().optional(),
  installCommand: z.string().optional(),
  outputDirectory: z.string().optional(),
  customDomain: z.string().optional(),
  customDomainDelete: z.boolean().optional(),
  autoDeployEnabled: z.boolean().optional(),
  useBuildCache: z.boolean().optional(),
  env: z.string().optional(),
  environmentVariables: z
    .array(
      z.object({
        key: z.string().min(1, "Key is required"),
        value: z.string(),
      }),
    )
    .optional(),
});

// Organization validations
export const updateOrganizationSchema = z.object({
  name: z
    .string()
    .min(1, "Organization name is required")
    .max(255, "Name must be less than 255 characters")
    .optional(),
  email: z
    .string()
    .email("Invalid email address")
    .max(255, "Email must be less than 255 characters")
    .optional(),
  company: z
    .string()
    .max(255, "Company name must be less than 255 characters")
    .optional(),
});

// Notification settings validations
export const notificationSettingsSchema = z.object({
  deployUpdates: z.boolean(),
  securityAlerts: z.boolean(),
  billing: z.boolean(),
  marketing: z.boolean(),
});

// Stripe checkout validations
export const stripeCheckoutSchema = z.object({
  nameOnCard: z.string().min(1, "Name on card is required"),
  cardNumber: z
    .string()
    .min(16, "Card number must be at least 16 digits")
    .max(19, "Card number is too long"),
  expiryDate: z
    .string()
    .regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Invalid expiry date format (MM/YY)"),
  cvv: z
    .string()
    .min(3, "CVV must be at least 3 digits")
    .max(4, "CVV must be at most 4 digits"),
});

export const domainSchema = z.object({
  domain: z
    .string()
    .regex(/^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/, "Invalid domain format"),
});

// Types for form data
export type DomainFormData = z.infer<typeof domainSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type UpdateProfileFormData = z.infer<typeof updateProfileSchema>;
export type CreateDeployFormData = z.infer<typeof createDeploySchema>;
export type UpdateDeploySettingsFormData = z.infer<
  typeof updateDeploySettingsSchema
>;
export type UpdateOrganizationFormData = z.infer<
  typeof updateOrganizationSchema
>;
export type NotificationSettingsFormData = z.infer<
  typeof notificationSettingsSchema
>;
export type StripeCheckoutFormData = z.infer<typeof stripeCheckoutSchema>;
